import { useEffect, useMemo, useState } from "react";

import { createColumnHelper } from "@tanstack/react-table";
import { PencilIcon, TrashIcon } from "@heroicons/react/24/outline";

import Modal from "../../layouts/Modal";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import { Table } from "../../components/base/Table/Table";
import ConfirmModal from "../../components/shared/Confirm/ConfirmModal";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import useDefaultCRUDHandlers from "../../hooks/useDefaultCRUDHandlers";
import useFeatureMutations from "../../data/Feature/useFeatureMutations";
import { FeatureStatus, IFeature } from "../../models/Feature/Feature";
import { FeatureQueryKeys } from "../../data/Feature/keys";
import { enumToSelectOptions } from "../../utils/enumToKeyValue";

const colHelper = createColumnHelper<IFeature>();

export default function FeaturesPage() {
  const { setInfo } = usePageNameContext();
  const { saveHandlers, deleteHandlers } = useDefaultCRUDHandlers("Feature");
  const { create, update, remove } = useFeatureMutations();

  const [createName, setCreateName] = useState("");
  const [createStatus, setCreateStatus] = useState<FeatureStatus>(FeatureStatus.Enabled);

  const [editOpen, setEditOpen] = useState(false);
  const [editingFeature, setEditingFeature] = useState<IFeature | null>(null);
  const [editName, setEditName] = useState("");
  const [editStatus, setEditStatus] = useState<FeatureStatus>(FeatureStatus.Enabled);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingFeature, setDeletingFeature] = useState<IFeature | null>(null);

  const statusOptions = useMemo(() => enumToSelectOptions(FeatureStatus), []);

  useEffect(() => {
    setInfo({
      name: "Features",
      desc: "Create, edit and delete features",
      pageTitle: "Features",
    });
  }, []);

  const handleCreateFeature = () => {
    const trimmedName = createName.trim();
    if (!trimmedName) return;

    create.mutate(
      {
        name: trimmedName,
        status: createStatus,
      },
      {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          setCreateName("");
          setCreateStatus(FeatureStatus.Enabled);
        },
      },
    );
  };

  const handleOpenEditModal = (feature: IFeature) => {
    setEditingFeature(feature);
    setEditName(feature.name);
    setEditStatus(feature.status);
    setEditOpen(true);
  };

  const handleSaveEdit = () => {
    if (!editingFeature) return;

    const trimmedName = editName.trim();
    if (!trimmedName) return;

    update.mutate(
      {
        ...editingFeature,
        name: trimmedName,
        status: editStatus,
      },
      {
        ...saveHandlers,
        onSuccess: () => {
          saveHandlers.onSuccess();
          setEditOpen(false);
          setEditingFeature(null);
        },
      },
    );
  };

  const handleDeleteFeature = (feature: IFeature) => {
    setDeletingFeature(feature);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = () => {
    if (!deletingFeature) return;

    remove.mutate(deletingFeature.id, {
      ...deleteHandlers,
      onSuccess: () => {
        deleteHandlers.onSuccess();
        setDeleteOpen(false);
        setDeletingFeature(null);
      },
    });
  };

  const cols = [
    colHelper.accessor("name", { header: "Feature Name" }),
    colHelper.accessor("status", {
      header: "Status",
      cell: (info) => statusOptions[info.row.original.status]?.label ?? info.row.original.status,
    }),
    colHelper.display({
      header: "Actions",
      cell: (info) => {
        const feature = info.row.original;
        return (
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="p-1 rounded hover:text-blue-700"
              onClick={() => handleOpenEditModal(feature)}
              title="Edit feature"
            >
              <PencilIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
            <button
              type="button"
              className="p-1 rounded hover:text-red-700"
              onClick={() => handleDeleteFeature(feature)}
              disabled={remove.isPending}
              title="Delete feature"
            >
              <TrashIcon className="w-5 h-5 opacity-70 hover:opacity-100" />
            </button>
          </div>
        );
      },
    }),
  ];

  return (
    <div className="flex flex-col w-full h-full gap-6">
      <ConfirmModal
        open={deleteOpen}
        setOpen={(open: boolean) => {
          setDeleteOpen(open);
          if (!open) {
            setDeletingFeature(null);
          }
        }}
        title={"Delete Feature?"}
        message={`Are you sure you want to delete feature "${deletingFeature?.name ?? ""}"?\n This is a soft delete.`}
        confirmButtonText={"Delete"}
        onConfirm={handleConfirmDelete}
        isLoading={remove.isPending}
      />
      <Modal open={editOpen} setOpen={setEditOpen} width="sm:max-w-xl">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-xl">Edit Feature</h1>
        </div>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="text-sm font-medium text-gray-500">Feature name</label>
            <input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border"
              placeholder="Enter feature name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <select
              value={editStatus}
              onChange={(e) => setEditStatus(parseInt(e.target.value, 10) as FeatureStatus)}
              className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border bg-white"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            classes="!bg-white !text-gray-700 !border !border-gray-300 hover:!bg-gray-50"
            onClick={() => setEditOpen(false)}
          />
          <ButtonInput
            classes=""
            label="Save"
            isSubmit={false}
            onClick={handleSaveEdit}
            disabled={update.isPending}
          />
        </div>
      </Modal>

      <div className="bg-white border border-gray-200 rounded p-4">
        <h3 className="text-lg font-semibold mb-3">Create Feature</h3>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_200px_auto] gap-2 items-end">
          <div>
            <label className="text-sm font-medium text-gray-500">Feature name</label>
            <input
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border"
              placeholder="Enter feature name"
            />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-500">Status</label>
            <select
              value={createStatus}
              onChange={(e) => setCreateStatus(parseInt(e.target.value, 10) as FeatureStatus)}
              className="w-full py-1 px-1 mt-1 text-sm text-gray-900 border border-border bg-white"
            >
              {statusOptions.map((status) => (
                <option key={status.value} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
          <ButtonInput
            label="Create Feature"
            isSubmit={false}
            onClick={handleCreateFeature}
            disabled={create.isPending}
            classes="h-fit"
          />
        </div>
      </div>

      <Table<IFeature> url="/feature" queryKey={FeatureQueryKeys.all()} columns={cols} />
    </div>
  );
}
